<script setup lang="ts">
  import type { IMainFooter } from '@/components/main-footer/types'
  import {
    ORGANIZATION_NAME,
    ORGANIZATION_LEGAL_NAME,
    ORGANIZATION_IATA_CODE,
    ORGANIZATION_FOUNDING_DATE
  } from '@/utils/constants/organization'

  const runtimeConfig = useRuntimeConfig()
  const route = useRoute()
  const { locale } = useI18n()

  const mainFooter = ref<IMainFooter>({
    logo: null,
    email: null,
    phone: null,
    address: null,
    contactUs: null,
    legalLinks: null,
    awards: null,
    copyright: null,
    paymentLinks: null,
    socials: null,
    googlePrivacyPolicy: '',
    machineTranslationDisclaimer: ''
  })

  const { data, error } = await useFetch<IMainFooter>('/api/storyblok/main-footer', {
    query: { language: locale.value }
  })

  if (!error.value && data.value) {
    mainFooter.value = data.value
  }

  const footerLinks = ref<any | null>(null)

  const { data: footerNavigation, error: footerNavigationError } = await useFetch('/api/storyblok/footer-navigation', {
    query: { language: locale.value }
  })

  if (!footerNavigationError.value && footerNavigation.value) {
    footerLinks.value = footerNavigation.value
  }

  const pageType = computed(() => (typeof route.meta.pageType === 'string' ? route.meta.pageType : ''))
  const isIndexPage = computed(() => pageType.value === 'index')

  const jsonLD = computed(() => {
    const siteUrl = trimLastSlash(runtimeConfig.public.siteUrl)
    const logoSrc = mainFooter.value.logo?.src

    const logo = logoSrc
      ? {
          '@type': 'ImageObject',
          '@id': `${siteUrl}/#logo`,
          url: logoSrc
        }
      : undefined

    const sameAsLinks = mainFooter.value.socials?.map((item) => item.link).filter(Boolean)
    const sameAs = sameAsLinks?.length ? sameAsLinks : undefined

    return {
      '@context': 'https://schema.org',
      '@type': 'Airline',
      '@id': `${siteUrl}/#organization`,
      name: ORGANIZATION_NAME,
      legalName: ORGANIZATION_LEGAL_NAME,
      url: runtimeConfig.public.siteUrl,
      iataCode: ORGANIZATION_IATA_CODE,
      foundingDate: ORGANIZATION_FOUNDING_DATE,
      logo,
      sameAs
    }
  })

  useJsonLd('main-footer-json-ld', jsonLD, isIndexPage)
</script>
<template>
  <footer class="flex flex-col gap-8 bg-grey">
    <div class="container flex flex-col justify-start gap-22 md:flex-row xl:gap-40">
      <MainFooterMenu :footer-links="footerLinks" />
    </div>
    <div class="container flex flex-col gap-8">
      <MainFooterPayments :payment-options="mainFooter.paymentLinks" />
      <MainFooterLegal :legal-links="mainFooter.legalLinks" />
      <MainFooterDisclaimers
        :google-privacy-policy="mainFooter.googlePrivacyPolicy"
        :machine-translation-disclaimer="mainFooter.machineTranslationDisclaimer"
      />
    </div>
    <div class="bg-bt-blue-dark px-4 pb-16 text-white no-underline md:pb-0">
      <div
        class="container flex flex-col items-start justify-between gap-8 border-b border-white/10 px-10 py-8 sm:flex-row sm:items-center sm:gap-10 sm:px-5 sm:py-10"
      >
        <MainFooterAward
          v-for="award in mainFooter.awards"
          :key="`award-${award._uid}`"
          :award="award"
        />
      </div>

      <div class="container flex flex-col items-center justify-between gap-4 py-8 md:flex-row md:py-7">
        <LazyNuxtLink
          v-if="mainFooter.copyright"
          class="order-2 text-sm/5 text-border no-underline md:order-1"
          :href="mainFooter.copyright.href"
          :target="mainFooter.copyright.target"
          external
        >
          {{ mainFooter.copyright.text }}
        </LazyNuxtLink>
        <div class="order-1 flex flex-col items-center gap-8 md:order-2 md:flex-row">
          <LazyNuxtLink
            v-if="mainFooter.contactUs"
            class="order-2 uppercase md:order-1 md:no-underline"
            :href="mainFooter.contactUs.href"
            :target="mainFooter.contactUs.target"
            external
          >
            {{ mainFooter.contactUs.text }}
          </LazyNuxtLink>
          <MainFooterSocials :social="mainFooter.socials" />
        </div>
      </div>
    </div>
  </footer>
</template>
